import React, { useState } from 'react';
import { Container, GridItem, Box, useTheme } from '@chakra-ui/react';
import { kebabCase, chunk } from 'lodash';
import { useEffectOnce } from 'react-use';
import { getPageId } from '../../../../../utils/pagination';
import { DeptGrid } from '@repo/ui-components/dept/design-system';
import AuthorCard from '@repo/ui-components/dept/atoms/AuthorCard';
import Pagination from '../../../../tailwind/Pagination';
import { useUiComponentsInternationalContext } from '@repo/static-translations';

import BlogAuthors from '../../../../../../sites/petsafe/src/content/allAuthors.json';

interface Block {
  _type: string;
  _key: string;
  authors: [
    {
      _id: string;
      language: string;
      image: {
        asset: {
          url: string;
        };
      };
      title: string;
      occupation: string;
      slug: {
        current: string;
      };
    }
  ];
}

interface BlogAuthorsProps {
  _id: string;
  language: string;
  _key: string;
  slug: {
    current: string;
  };
  title: string;
  occupation: string;
  image: {
    alt: string;
    asset: {
      _id: string;
      url: string;
    };
  };
}

interface Location {
  pathname: string;
  search: string;
}

interface GridBlogAuthorsProps {
  block: Block;
  location: Location;
}

export default function GridBlogAuthors({
  block,
  location,
}: GridBlogAuthorsProps) {
  const { locale } = useUiComponentsInternationalContext();
  const theme = useTheme();

  const items = block?.authors?.length
    ? block.authors
    : BlogAuthors.filter(author => author.language === locale);
  const [chunks, setChunks] = useState<BlogAuthorsProps[][]>([]);
  const pageId = getPageId(location);
  const pageElements = chunks[parseInt(pageId + 1, 10) - 1 || 0] || [];

  const getAuthorsChunks = (): BlogAuthorsProps[][] => {
    const chunkSize = 16;
    return chunk(items, chunkSize);
  };

  useEffectOnce(() => {
    setChunks(getAuthorsChunks());
  });

  return (
    <Box
      bgSize="cover"
      bgRepeat="no-repeat"
      bgImg={theme.images.ellipse}
      backgroundSize={['66%', null, '36%']}
      backgroundPosition={['right 4%', null, 'right 38%']}
      as="section"
      id={`${kebabCase(block._type)}_${block._key}`}
    >
      <Container py={[14, null, 16]}>
        <DeptGrid my={10} flexBasis="100%" gridRowGap={[6, null, 8]}>
          {pageElements.map(author => (
            <GridItem colSpan={[6, null, 3]} key={author._id}>
              <AuthorCard author={author} />
            </GridItem>
          ))}
        </DeptGrid>
        <Pagination numOfPages={chunks.length} page={pageId} />
      </Container>
    </Box>
  );
}
